import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, scopeKey, type ChatScopeRef } from "@/lib/api";
import {
  type SettingsChatMessage,
  type SettingsChatThread,
  type SettingsProposal,
} from "@/lib/types";

/** The conversation a practice has with its own settings.
 *
 *  Two things about the shape of this are load-bearing rather than stylistic.
 *
 *  A change is never applied by the turn that proposed it. The assistant answers, and if it is offering something the
 *  answer arrives with a proposal attached; the proposal is stored server-side against that message and this panel
 *  sends back only its id. So what gets written is what the practice read and pressed the button under, and nothing
 *  here — or in anything that manages to talk to this endpoint — can substitute a different change.
 *
 *  The transcript is the server's rows, not this component's state. Past conversations open read-only from the same
 *  data the model was given, which is what makes the history worth keeping.
 */

export const SettingsChatPanel = ({ scope }: { scope: ChatScopeRef }) => {
  // One cache key per scope, so an admin conversation and a practice's cannot serve each other's rows.
  const key = scopeKey(scope);
  const qc = useQueryClient();
  const [threadId, setThreadId] = useState<string | undefined>();
  const [turns, setTurns] = useState<SettingsChatMessage[]>([]);
  const [draft, setDraft] = useState("");
  const [view, setView] = useState<"chat" | "history">("chat");
  const [openThreadId, setOpenThreadId] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const send = useMutation({
    mutationFn: (message: string) => api.settingsChat(scope, message, threadId),
    // The message goes on screen the moment it is sent, not when the answer arrives. A reply takes several seconds,
    // and a chat that swallows what you typed for that long reads as having lost it — people retype, or send again.
    // The id is local and provisional; the server's row is never shown for the user's own turn, only for the
    // assistant's, which is the one `/apply` has to name.
    onMutate: (message) => {
      const pending = localTurn("user", message);
      setTurns((t) => [...t, pending]);
      setDraft("");
      return { pendingId: pending.id };
    },
    // Take the optimistic turn back off and hand the text back to the box. The practice has just typed a paragraph
    // describing what they want changed; retyping it is the difference between a hiccup and giving up on the feature.
    onError: (_err, message, context) => {
      setTurns((t) => t.filter((m) => m.id !== context?.pendingId));
      setDraft((current) => (current === "" ? message : current));
    },
    onSuccess: (res) => {
      setThreadId(res.threadId);
      setTurns((t) => [
        ...t,
        {
          id: res.messageId,
          role: "assistant",
          content: res.reply,
          proposal: res.proposal ?? null,
          applied: false,
          createdAt: new Date().toISOString(),
        },
      ]);
      void qc.invalidateQueries({ queryKey: ["settings-chat-threads", key] });
    },
  });

  const apply = useMutation({
    mutationFn: (messageId: string) => {
      if (!threadId) throw new Error("Konverzace ještě nezačala.");
      return api.applySettingsProposal(scope, threadId, messageId);
    },
    // A refused apply can still have written something: the sections commit one at a time, so a failure part-way
    // through leaves the ones before it saved and recorded. Refreshing on the error path too is what stops the page
    // describing a configuration the practice no longer has.
    onError: () => refreshDerived(),
    onSuccess: (_res, messageId) => {
      setTurns((t) => t.map((m) => (m.id === messageId ? { ...m, applied: true } : m)));
      refreshDerived();
    },
  });

  /** Everything that is downstream of the settings.
    *
    *  The practice itself; the summary, which is generated from it and sits directly above this panel; the change log;
    *  the stored transcript, which now records whether this turn was applied; and the practices list, since a change
    *  can rename or disable the practice the header switcher is showing.
    */
  function refreshDerived() {
    // The admin scope has no practice row to refresh; the shared surfaces it can change are the summary, the change
    // log and the keyword lists, so those are invalidated for both scopes.
    if (scope.kind === "practice") {
      void qc.invalidateQueries({ queryKey: ["practice", scope.practiceId] });
      void qc.invalidateQueries({ queryKey: ["practice-summary", scope.practiceId] });
    } else {
      void qc.invalidateQueries({ queryKey: ["admin-summary"] });
      void qc.invalidateQueries({ queryKey: ["system-prompts"] });
      void qc.invalidateQueries({ queryKey: ["decision-keywords"] });
    }
    void qc.invalidateQueries({ queryKey: ["settings-changes", key] });
    void qc.invalidateQueries({ queryKey: ["settings-chat-thread", key] });
    void qc.invalidateQueries({ queryKey: ["practices"] });
  }

  // Follow the conversation down as it grows, including while an answer is being waited for — the "thinking" line is
  // the thing somebody is watching.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [turns, send.isPending]);

  const submit = () => {
    const message = draft.trim();
    if (message === "" || send.isPending) return;
    // The draft is cleared in `onMutate`, alongside the turn being put on screen, so the two cannot disagree.
    send.mutate(message);
  };

  const startNew = () => {
    setThreadId(undefined);
    setTurns([]);
    setDraft("");
    setView("chat");
    setOpenThreadId(null);
    // Mutation state outlives the conversation it belongs to, so without this the error from the last failed turn
    // greets the next one.
    send.reset();
    apply.reset();
  };

  return (
    <section className="flex h-[calc(100vh-11rem)] min-h-[28rem] flex-col rounded border border-gray-200 bg-white">
      <header className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500">
          {view === "chat" ? "Asistentka nastavení" : "Historie konverzací"}
        </h3>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setView(view === "chat" ? "history" : "chat")}
            className="text-xs text-[#1a2b6b] hover:underline"
          >
            {view === "chat" ? "Historie" : "Zpět do konverzace"}
          </button>
          {view === "chat" && turns.length > 0 && (
            <button
              type="button"
              onClick={startNew}
              className="text-xs text-gray-500 hover:text-[#1a2b6b]"
            >
              Nová konverzace
            </button>
          )}
        </div>
      </header>

      {view === "history" ? (
        <HistoryView
          scope={scope}
          openThreadId={openThreadId}
          onOpen={setOpenThreadId}
        />
      ) : (
        <>
          <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
            {turns.length === 0 && <EmptyState scope={scope} />}
            {turns.map((turn) => (
              <Turn
                key={turn.id}
                turn={turn}
                onApply={() => apply.mutate(turn.id)}
                applying={apply.isPending && apply.variables === turn.id}
                error={
                  apply.isError && apply.variables === turn.id
                    ? applyErrorText((apply.error as Error).message)
                    : undefined
                }
              />
            ))}
            {send.isPending && (
              <p className="text-xs text-gray-400" aria-live="polite">
                Přemýšlím…
              </p>
            )}
            {send.isError && (
              <p className="text-xs text-red-600">
                Asistentka teď neodpovídá ({(send.error as Error).message}). Zkuste to prosím znovu.
              </p>
            )}
          </div>

          <div className="border-t border-gray-200 p-3">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                // Enter sends, Shift+Enter breaks the line. These are short instructions, not documents.
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  submit();
                }
              }}
              rows={3}
        placeholder={
                scope.kind === "admin"
                  ? "Napište, co chcete změnit — třeba „přidej do maďarského seznamu ano slovo rendben“."
                  : "Napište, co chcete změnit — třeba „v pátek zavíráme už ve dvanáct“."
              }
              className="w-full resize-none rounded border border-gray-300 px-3 py-2 text-sm focus:border-[#1a2b6b] focus:outline-none"
            />
            <div className="mt-2 flex items-center justify-between">
              <span className="text-[11px] text-gray-400">
                Změny se ukládají až po vašem potvrzení.
              </span>
              <button
                type="button"
                onClick={submit}
                disabled={draft.trim() === "" || send.isPending}
                className="rounded bg-[#1a2b6b] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#0f1d52] disabled:cursor-not-allowed disabled:bg-gray-300"
              >
                {send.isPending ? "Odesílám…" : "Odeslat"}
              </button>
            </div>
          </div>
        </>
      )}
    </section>
  );
};

/** Czech for the codes the apply endpoint returns.
 *
 *  These reach the screen verbatim otherwise — `fetchJson` surfaces whatever `error` the API sent — and "already_applied"
 *  in front of a receptionist is not a message, it is a leak. Anything unrecognised is passed through, since a message
 *  nobody wrote a translation for is still better than a generic one that hides it.
 */
const applyErrorText = (raw: string): string => {
  if (raw.includes("already_applied"))
    return "Tato změna už byla uložena. Otevřete Historii, pokud si chcete ověřit co se změnilo.";
  if (raw.includes("admin_only"))
    return "Tohle nastavení mění podpora Emmy, ne ordinace. Napište nám a rádi to zařídíme.";
  if (raw.includes("proposal_not_found") || raw.includes("no_proposal_on_message"))
    return "Tuhle změnu už nelze uložit — zkuste o ni požádat znovu.";
  if (raw.includes("partiallyApplied"))
    return "Uložila se jen část změny. Co se povedlo, najdete v přehledu provedených změn níže.";
  return raw;
};

const EmptyState = ({ scope }: { scope: ChatScopeRef }) => (
  <div className="space-y-3 text-sm text-gray-600">
    <p>
      {scope.kind === "admin"
        ? "Zeptejte se, jak se co dělá, nebo rovnou napište, co chcete změnit ve sdíleném nastavení. Než cokoli uloží, shrne vám to a počká na potvrzení."
        : "Zeptejte se, jak asistentka funguje, nebo rovnou napište, co chcete změnit. Než cokoli uloží, shrne vám to a počká na potvrzení."}
    </p>
    {/* The examples are what teaches somebody what this box is for, so they have to be things THIS scope can actually
        do. The practice ones on the admin page read as an invitation to change a surgery's opening hours from a page
        that would refuse it. */}
    <ul className="list-disc space-y-1 pl-5 text-xs text-gray-500">
      {(scope.kind === "admin"
        ? [
            "„Přidej do maďarského seznamu souhlasu slovo rendben.“",
            "„Přeformuluj is-that-all-3, ať zní méně formálně.“",
            "„Jak se zakládá nová ordinace?“",
          ]
        : [
            "„Ve středu máme ordinaci jen do poledne.“",
            "„Přidej mezi časté dotazy, kde se dá zaparkovat.“",
            "„Co se stane, když zavolá někdo, koho nemáme v kartotéce?“",
          ]
      ).map((example) => (
        <li key={example}>{example}</li>
      ))}
    </ul>
  </div>
);

const Turn = ({
  turn,
  onApply,
  applying,
  error,
}: {
  turn: SettingsChatMessage;
  onApply: () => void;
  applying: boolean;
  error?: string;
}) => {
  if (turn.role === "user") {
    return (
      <div className="flex justify-end">
        <p className="max-w-[85%] whitespace-pre-wrap rounded-lg rounded-br-sm bg-[#1a2b6b] px-3 py-2 text-sm text-white">
          {turn.content}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <p className="max-w-[92%] whitespace-pre-wrap text-sm leading-relaxed text-gray-700">
        {turn.content}
      </p>
      {turn.proposal && (
        <ProposalCard
          proposal={turn.proposal}
          applied={turn.applied}
          onApply={onApply}
          applying={applying}
          error={error}
        />
      )}
    </div>
  );
};

/** The confirmation prompt.
 *
 *  Everything the practice needs in order to agree, and nothing they have to go elsewhere to find: what changes, what
 *  it will cost them if it is wrong, and the fact that putting it back is not a button. The no-undo line is rendered
 *  here rather than left to the assistant's own wording, so it is present on every proposal regardless of what the
 *  model chose to mention.
 */
const ProposalCard = ({
  proposal,
  applied,
  onApply,
  applying,
  error,
}: {
  proposal: SettingsProposal;
  applied: boolean;
  onApply: () => void;
  applying: boolean;
  error?: string;
}) => (
  <div className="rounded border border-amber-300 bg-amber-50 p-3">
    <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-amber-800">
      Chystaná změna
    </p>
    <p className="whitespace-pre-wrap text-sm text-amber-900">{proposal.summary}</p>

    {proposal.warnings.length > 0 && (
      <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-amber-800">
        {proposal.warnings.map((w, i) => (
          <li key={i}>{w}</li>
        ))}
      </ul>
    )}

    <p className="mt-2 text-xs text-amber-800">
      Vrátit zpět jedním tlačítkem nejde. Změna se zapíše do historie i s původním nastavením, takže
      ji lze vrátit ručně.
    </p>

    {applied ? (
      <p className="mt-3 text-xs font-medium text-green-700" aria-live="polite">
        Uloženo. Asistentka se podle toho začne chovat během chvilky.
      </p>
    ) : (
      <div className="mt-3 flex items-center gap-3">
        <button
          type="button"
          onClick={onApply}
          disabled={applying}
          className="rounded bg-amber-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-800 disabled:cursor-not-allowed disabled:bg-gray-300"
        >
          {applying ? "Ukládám…" : "Potvrdit a uložit"}
        </button>
        <span className="text-xs text-amber-800">
          Nechcete-li to, napište dál — bez potvrzení se nic nezmění.
        </span>
      </div>
    )}
    {error && <p className="mt-2 text-xs text-red-700">{error}</p>}
  </div>
);

const HistoryView = ({
  scope,
  openThreadId,
  onOpen,
}: {
  scope: ChatScopeRef;
  openThreadId: string | null;
  onOpen: (id: string | null) => void;
}) => {
  const threads = useQuery({
    queryKey: ["settings-chat-threads", scopeKey(scope)],
    queryFn: () => api.listSettingsChatThreads(scope),
  });

  const transcript = useQuery({
    queryKey: ["settings-chat-thread", scopeKey(scope), openThreadId],
    queryFn: () => api.getSettingsChatTranscript(scope, openThreadId as string),
    enabled: openThreadId !== null,
  });

  if (openThreadId !== null) {
    return (
      <div className="flex-1 overflow-y-auto px-4 py-4">
        <button
          type="button"
          onClick={() => onOpen(null)}
          className="mb-3 text-xs text-[#1a2b6b] hover:underline"
        >
          ← Zpět na seznam
        </button>
        {transcript.isPending && <p className="text-xs text-gray-500">Načítám…</p>}
        {transcript.isError && (
          <p className="text-xs text-red-600">
            Chyba: {(transcript.error as Error).message}
          </p>
        )}
        <div className="space-y-4">
          {transcript.data?.messages.map((m) => (
            <div key={m.id} className="space-y-2">
              {m.role === "user" ? (
                <div className="flex justify-end">
                  <p className="max-w-[85%] whitespace-pre-wrap rounded-lg rounded-br-sm bg-gray-100 px-3 py-2 text-sm text-gray-700">
                    {m.content}
                  </p>
                </div>
              ) : (
                <p className="max-w-[92%] whitespace-pre-wrap text-sm leading-relaxed text-gray-700">
                  {m.content}
                </p>
              )}
              {m.proposal && (
                <div className="rounded border border-gray-200 bg-gray-50 p-3">
                  <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                    {m.applied ? "Změna byla potvrzena" : "Změna nebyla potvrzena"}
                  </p>
                  <p className="whitespace-pre-wrap text-xs text-gray-600">{m.proposal.summary}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto px-4 py-4">
      {threads.isPending && <p className="text-xs text-gray-500">Načítám…</p>}
      {threads.isError && (
        <p className="text-xs text-red-600">Chyba: {(threads.error as Error).message}</p>
      )}
      {threads.data?.length === 0 && (
        <p className="text-xs text-gray-500">Zatím tu není žádná konverzace.</p>
      )}
      <ul className="space-y-1">
        {threads.data?.map((t) => (
          <li key={t.id}>
            <button
              type="button"
              onClick={() => onOpen(t.id)}
              className="w-full rounded border border-gray-200 px-3 py-2 text-left hover:border-[#1a2b6b] hover:bg-gray-50"
            >
              <span className="block truncate text-sm text-gray-700">{t.title || "Bez názvu"}</span>
              <span className="mt-0.5 block text-[11px] text-gray-400">
                {threadSubtitle(t)}
              </span>
            </button>
          </li>
        ))}
      </ul>
      <p className="mt-4 text-[11px] text-gray-400">
        Konverzace se uchovávají tři roky.
      </p>
    </div>
  );
};

const threadSubtitle = (t: SettingsChatThread): string =>
  `${new Date(t.lastMessageAt).toLocaleDateString("cs-CZ", {
    day: "numeric",
    month: "numeric",
    year: "numeric",
  })} · ${t.actorEmail}`;

/** The user's own turn, rendered before the server has assigned it an id.
 *
 *  The id only has to be unique among the turns on screen, and a stored message id would arrive one round trip too
 *  late — the point of putting it there immediately is that the message appears the moment it is sent.
 */
const localTurn = (role: "user" | "assistant", content: string): SettingsChatMessage => ({
  id: `local-${role}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  role,
  content,
  proposal: null,
  applied: false,
  createdAt: new Date().toISOString(),
});
